import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { MapPin, MessageCircle, Gavel, Shield, Users, Moon, Mail, Menu, X, User, Gamepad2, Music, Palette, Sparkles } from 'lucide-react';
import { SEO } from '@/components/seo/SEO';
import { useTranslation } from 'react-i18next';
import { jaHome } from '@/lib/locales/public-ja';

const scrollToSection = (id: string) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};


const useInView = (threshold = 0.2) => {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
};

const FloatingMockCard = () => (
  <div className="relative animate-float-card" style={{ willChange: 'transform' }}>
    {/* Subtle glow behind card */}
    <div className="absolute inset-0 rounded-3xl bg-primary/15 blur-xl scale-110" />
    
    <div className="relative w-56 h-72 md:w-64 md:h-80 rounded-3xl overflow-hidden glass border border-primary-foreground/10 shadow-2xl">
      {/* Mock photo area */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-premium/20 to-primary/40">
        <div className="absolute inset-0 flex items-center justify-center">
          <User className="w-20 h-20 text-primary-foreground/30" />
        </div>
      </div>
      
      {/* Photo indicators */}
      <div className="absolute top-3 left-3 right-3 flex gap-1">
        <div className="h-1 flex-[2] rounded-full bg-primary-foreground/80" />
        <div className="h-1 flex-1 rounded-full bg-primary-foreground/30" />
        <div className="h-1 flex-1 rounded-full bg-primary-foreground/30" />
      </div>
      
      {/* Bottom info overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-4 glass-heavy">
        <div className="flex items-center gap-1">
          <div className="h-3 w-20 rounded bg-primary-foreground/60" />
          <div className="h-3 w-8 rounded bg-primary-foreground/40" />
        </div>
        <div className="flex items-center gap-1 mt-2">
          <MapPin className="w-3 h-3 text-primary-foreground/50" />
          <div className="h-2 w-16 rounded bg-primary-foreground/30" />
        </div>
      </div>
      
      {/* SMASH stamp */}
      <div className="absolute top-8 left-4 border-4 border-match rounded-lg px-3 py-1 rotate-[-20deg] opacity-70">
        <span className="text-match font-extrabold text-xl tracking-wider">SMASH!</span>
      </div>
    </div>
  </div>
);

const Index = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isJa = i18n.resolvedLanguage?.startsWith('ja');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Capture referral code from URL and persist
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      sessionStorage.setItem('referral_code', ref);
    }
  }, []);

  const featuresView = useInView();
  const howItWorksView = useInView();
  const highlightsView = useInView();
  const safetyView = useInView();

  const navItems = [
    { id: 'features', label: 'Features' },
    { id: 'how-it-works', label: 'How It Works' },
    { id: 'safety', label: 'Safety' },
    { id: 'contact', label: 'Contact' },
  ].map((item, index) => ({ ...item, label: isJa ? jaHome.nav[index] : item.label }));

  const handleNavClick = (id: string) => {
    scrollToSection(id);
    setMobileMenuOpen(false);
  };

  return <div className="min-h-screen" style={{ background: 'hsl(250 22% 12%)' }}>
      <SEO
        title={isJa ? jaHome.seoTitle : 'Smash or Pass Dating App | Match, Chat & Connect'}
        description={isJa ? jaHome.seoDescription : 'Meet nearby adults, match mutually, chat, compare compatibility, and personalize your dating profile with Smash or Pass.'}
        path="/"
      />
      {/* Header - Frosted Glass */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-heavy">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full gradient-love flex items-center justify-center shadow-glow">
              <Gavel className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="text-xl font-extrabold text-gradient">Smash or Pass</span>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className="text-sm text-white/50 hover:text-white transition-colors font-medium"
              >
                {item.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" className="hidden sm:inline-flex spring-press border-white/20 text-white hover:bg-white/10">
              <Link to="/games">
                <Gamepad2 className="w-4 h-4 mr-2" /> {isJa ? jaHome.games : 'Games'}
              </Link>
            </Button>
            <Button onClick={() => navigate('/auth')} className="gradient-love text-primary-foreground hidden sm:inline-flex spring-press shadow-glow">
              {isJa ? jaHome.getStarted : 'Get Started'}
            </Button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-foreground"
              aria-label={isJa ? jaHome.toggleMenu : 'Toggle menu'}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden glass-heavy border-t border-border/30">
            <nav className="flex flex-col px-6 py-4 gap-4">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className="text-left text-muted-foreground hover:text-foreground transition-colors py-2"
                >
                  {item.label}
                </button>
              ))}
              <Button asChild variant="outline" className="w-full mt-2 border-white/20 text-white hover:bg-white/10" onClick={() => setMobileMenuOpen(false)}>
                <Link to="/games">
                  <Gamepad2 className="w-4 h-4 mr-2" /> {isJa ? jaHome.games : 'Games'}
                </Link>
              </Button>
              <Button onClick={() => { navigate('/auth'); setMobileMenuOpen(false); }} className="gradient-love text-primary-foreground w-full mt-2 spring-press">
                {isJa ? jaHome.getStarted : 'Get Started'}
              </Button>
            </nav>
          </div>
        )}
      </header>

      {/* Hero Section - Gradient Mesh */}
      <section className="relative min-h-screen flex items-center justify-center px-6 pt-20 overflow-hidden">
        {/* Nebula background */}
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse 90% 70% at 20% 30%, hsl(270 55% 40% / 0.3) 0%, transparent 65%),
            radial-gradient(ellipse 80% 60% at 80% 20%, hsl(200 80% 50% / 0.25) 0%, transparent 60%),
            radial-gradient(ellipse 70% 80% at 50% 80%, hsl(320 45% 35% / 0.2) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 65% 55%, hsl(175 65% 45% / 0.18) 0%, transparent 55%),
            hsl(250 22% 12%)
          `
        }} />
        {/* Star field */}
        <div className="absolute inset-0 opacity-55" style={{
          backgroundImage: `
            radial-gradient(2px 2px at 12% 15%, hsl(200 90% 92%) 0%, transparent 100%),
            radial-gradient(1.5px 1.5px at 35% 55%, hsl(280 70% 88%) 0%, transparent 100%),
            radial-gradient(1px 1px at 55% 8%, hsl(190 80% 90%) 0%, transparent 100%),
            radial-gradient(1.5px 1.5px at 78% 35%, hsl(320 60% 85%) 0%, transparent 100%),
            radial-gradient(1px 1px at 92% 75%, hsl(265 60% 85%) 0%, transparent 100%),
            radial-gradient(2px 2px at 18% 80%, hsl(190 80% 90%) 0%, transparent 100%),
            radial-gradient(1px 1px at 42% 30%, hsl(280 80% 90%) 0%, transparent 100%),
            radial-gradient(1px 1px at 68% 88%, hsl(200 80% 88%) 0%, transparent 100%)
          `
        }} />
        
        <div className="relative z-10 max-w-6xl mx-auto w-full flex flex-col md:flex-row items-center gap-12 md:gap-16">
          {/* Left: Typography */}
          <div className="flex-1 text-center md:text-left">
            <div className="animate-fade-in-up">
              <h1 className="text-6xl md:text-7xl lg:text-8xl font-extrabold tracking-tighter leading-[0.9]">
                <span className="text-gradient">SMASH</span>
                <br />
                <span className="text-white/60 font-light italic text-4xl md:text-5xl lg:text-6xl">or</span>
                <br />
                <span className="text-white">PASS</span>
                <span className="sr-only"> {isJa ? jaHome.heroSuffix : '— Free Dating App to Find Your Match Today'}</span>
              </h1>
              <p className="text-xl md:text-2xl text-white/50 mt-6 max-w-lg leading-relaxed">
                 {isJa ? jaHome.heroBody : 'Meet nearby adults, make mutual matches, and start real conversations in a dating app built for personality.'}
              </p>
            </div>
            
            <div className="mt-8 flex flex-col sm:flex-row gap-4 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <Button size="lg" className="gradient-love text-primary-foreground shadow-glow text-lg h-14 px-8 spring-press" onClick={() => navigate('/auth')}>
                {isJa ? jaHome.heroCta : 'Start Matching Now'}
              </Button>
              <p className="text-sm text-white/40 self-center">
                 {isJa ? jaHome.freeNote : 'Free to join • Optional in-app purchases'}
              </p>
            </div>
          </div>

          {/* Right: Floating card mockup */}
          <div className="flex-shrink-0 animate-fade-in-up" style={{ animationDelay: '300ms' }}>
            <FloatingMockCard />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 relative overflow-hidden" ref={featuresView.ref} style={{
        background: `
          linear-gradient(180deg, hsl(var(--background)) 0%, hsl(240 20% 14%) 30%, hsl(250 22% 12%) 100%)
        `
      }}>
        {/* Nebula clouds */}
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse 80% 60% at 15% 40%, hsl(280 60% 45% / 0.25) 0%, transparent 70%),
            radial-gradient(ellipse 70% 50% at 85% 30%, hsl(200 80% 50% / 0.2) 0%, transparent 65%),
            radial-gradient(ellipse 90% 70% at 50% 85%, hsl(320 50% 40% / 0.18) 0%, transparent 60%),
            radial-gradient(ellipse 60% 40% at 70% 70%, hsl(175 65% 45% / 0.15) 0%, transparent 55%)
          `
        }} />
        {/* Subtle star dust */}
        <div className="absolute inset-0 opacity-50" style={{
          backgroundImage: `
            radial-gradient(1.5px 1.5px at 10% 20%, hsl(200 90% 90%) 0%, transparent 100%),
            radial-gradient(1px 1px at 30% 60%, hsl(280 70% 85%) 0%, transparent 100%),
            radial-gradient(2px 2px at 50% 10%, hsl(190 80% 92%) 0%, transparent 100%),
            radial-gradient(1px 1px at 70% 40%, hsl(320 60% 80%) 0%, transparent 100%),
            radial-gradient(1px 1px at 90% 80%, hsl(265 60% 80%) 0%, transparent 100%),
            radial-gradient(1.5px 1.5px at 15% 85%, hsl(190 80% 88%) 0%, transparent 100%),
            radial-gradient(1px 1px at 85% 15%, hsl(175 70% 80%) 0%, transparent 100%),
            radial-gradient(1px 1px at 45% 45%, hsl(280 80% 88%) 0%, transparent 100%)
          `
        }} />
        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-5xl font-extrabold text-center mb-4 text-white">
            {isJa ? jaHome.featuresHeading : <>Why Choose <span className="text-gradient">Smash or Pass</span>?</>}
          </h2>
          <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto text-lg">
            {isJa ? jaHome.featuresBody : "We've reimagined online dating to be more authentic, fun, and effective."}
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: MapPin, title: 'Location-Based Matching', desc: 'Find people near you who share your interests. Meet locals and make real connections in your area.', delay: 0 },
               { icon: Moon, title: 'Everything included', desc: 'Seeing who liked you, rewind, boosts, and daily 4pm pairings are free for everyone. No subscriptions, no paid tiers.', delay: 150 },
              { icon: MessageCircle, title: 'Real-Time Messaging', desc: 'Connect instantly with your matches through our seamless, real-time chat system.', delay: 300 },
            ].map((item, index) => ({ ...item, title: isJa ? jaHome.features[index][0] : item.title, desc: isJa ? jaHome.features[index][1] : item.desc })).map(({ icon: Icon, title, desc, delay }) => (
              <div
                key={title}
                className={`p-8 rounded-3xl border border-white/10 shadow-card transition-all duration-500 ${
                  featuresView.inView ? 'animate-slide-up-fade opacity-100' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  animationDelay: `${delay}ms`,
                  background: 'linear-gradient(135deg, hsl(260 25% 20% / 0.7), hsl(240 22% 16% / 0.8))',
                }}
              >
                <div className="w-16 h-16 rounded-2xl mb-6 flex items-center justify-center" style={{ background: 'hsl(200 80% 55% / 0.15)' }}>
                  <Icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
                <p className="text-white/50 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-6 relative overflow-hidden" ref={howItWorksView.ref} style={{
        background: 'hsl(250 22% 12%)'
      }}>
        {/* Nebula clouds */}
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse 70% 60% at 25% 50%, hsl(200 70% 50% / 0.22) 0%, transparent 65%),
            radial-gradient(ellipse 80% 50% at 75% 30%, hsl(290 55% 45% / 0.2) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 50% 90%, hsl(175 65% 45% / 0.15) 0%, transparent 55%)
          `
        }} />
        {/* Star dust */}
        <div className="absolute inset-0 opacity-45" style={{
          backgroundImage: `
            radial-gradient(1.5px 1.5px at 20% 30%, hsl(200 90% 90%) 0%, transparent 100%),
            radial-gradient(1px 1px at 60% 70%, hsl(280 70% 85%) 0%, transparent 100%),
            radial-gradient(2px 2px at 80% 20%, hsl(190 80% 92%) 0%, transparent 100%),
            radial-gradient(1px 1px at 40% 90%, hsl(320 60% 80%) 0%, transparent 100%),
            radial-gradient(1px 1px at 5% 60%, hsl(200 80% 85%) 0%, transparent 100%)
          `
        }} />
        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-5xl font-extrabold text-center mb-4 text-white">
            {isJa ? jaHome.howHeading : <>How It <span className="text-gradient">Works</span></>}
          </h2>
          <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto text-lg">
            {isJa ? jaHome.howBody : 'Getting started is easy. Find your match in three simple steps.'}
          </p>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { num: '1', title: 'Create Your Profile', desc: 'Sign up and create your profile with photos and a bio that shows off your personality.', delay: 0 },
              { num: '2', title: 'Swipe & Match', desc: 'Browse profiles and swipe to the right for people you like. When they like you back, it\'s a match!', delay: 150 },
              { num: '3', title: 'Start Chatting', desc: 'Message your matches and take the conversation wherever it leads. Your next great story starts here.', delay: 300 },
            ].map((item, index) => ({ ...item, title: isJa ? jaHome.steps[index][0] : item.title, desc: isJa ? jaHome.steps[index][1] : item.desc })).map(({ num, title, desc, delay }) => (
              <div
                key={num}
                className={`text-center p-8 rounded-3xl border border-white/10 transition-all duration-500 ${
                  howItWorksView.inView ? 'animate-slide-up-fade opacity-100' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  animationDelay: `${delay}ms`,
                  background: 'linear-gradient(135deg, hsl(260 25% 20% / 0.65), hsl(250 22% 16% / 0.75))',
                }}
              >
                <div className="w-16 h-16 rounded-2xl gradient-love text-primary-foreground mx-auto mb-6 flex items-center justify-center text-2xl font-extrabold shadow-glow">
                  {num}
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
                <p className="text-white/50 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Highlights Section */}
      <section id="highlights" className="py-24 px-6 relative overflow-hidden" ref={highlightsView.ref} style={{
        background: 'hsl(252 21% 13%)'
      }}>
        {/* Nebula clouds */}
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse 80% 55% at 20% 30%, hsl(280 60% 45% / 0.22) 0%, transparent 65%),
            radial-gradient(ellipse 70% 60% at 80% 60%, hsl(200 80% 50% / 0.18) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 50% 85%, hsl(175 65% 45% / 0.15) 0%, transparent 55%)
          `
        }} />
        {/* Star dust */}
        <div className="absolute inset-0 opacity-45" style={{
          backgroundImage: `
            radial-gradient(1.5px 1.5px at 15% 20%, hsl(200 90% 90%) 0%, transparent 100%),
            radial-gradient(1px 1px at 40% 70%, hsl(280 70% 85%) 0%, transparent 100%),
            radial-gradient(2px 2px at 65% 15%, hsl(190 80% 92%) 0%, transparent 100%),
            radial-gradient(1px 1px at 85% 80%, hsl(320 60% 80%) 0%, transparent 100%),
            radial-gradient(1px 1px at 5% 55%, hsl(200 80% 85%) 0%, transparent 100%)
          `
        }} />
        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-5xl font-extrabold text-center mb-4 text-white">
            {isJa ? jaHome.highlightsHeading : <>Express Yourself & <span className="text-gradient">Connect Deeper</span></>}
          </h2>
          <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto text-lg">
            {isJa ? jaHome.highlightsBody : 'More than just photos — show who you are and find people who actually fit you.'}
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: Music, title: 'Theme Song', desc: 'Add your favorite Spotify, YouTube, or SoundCloud track to your profile so matches hear your vibe before they message.', delay: 0 },
              { icon: Palette, title: 'Custom Profiles & Backgrounds', desc: 'Personalize your profile with custom colors, gradients, and background images that match your style.', delay: 150 },
              { icon: Sparkles, title: 'Compatibility Test', desc: 'Take a 3-minute quiz based on Big Five personality and attachment science, then see your compatibility score with every match.', delay: 300 },
            ].map((item, index) => ({ ...item, title: isJa ? jaHome.highlights[index][0] : item.title, desc: isJa ? jaHome.highlights[index][1] : item.desc })).map(({ icon: Icon, title, desc, delay }) => (
              <div
                key={title}
                className={`p-8 rounded-3xl border border-white/10 shadow-card transition-all duration-500 ${
                  highlightsView.inView ? 'animate-slide-up-fade opacity-100' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  animationDelay: `${delay}ms`,
                  background: 'linear-gradient(135deg, hsl(260 25% 20% / 0.7), hsl(240 22% 16% / 0.8))',
                }}
              >
                <div className="w-16 h-16 rounded-2xl mb-6 flex items-center justify-center" style={{ background: 'hsl(200 80% 55% / 0.15)' }}>
                  <Icon className="w-8 h-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">{title}</h3>
                <p className="text-white/50 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Section */}
      <section id="safety" className="py-24 px-6 relative overflow-hidden" ref={safetyView.ref} style={{
        background: 'hsl(255 20% 11%)'
      }}>
        {/* Nebula clouds */}
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse 75% 55% at 80% 60%, hsl(175 60% 45% / 0.2) 0%, transparent 65%),
            radial-gradient(ellipse 65% 50% at 20% 35%, hsl(270 55% 50% / 0.22) 0%, transparent 60%),
            radial-gradient(ellipse 80% 40% at 55% 15%, hsl(210 70% 50% / 0.15) 0%, transparent 55%),
            radial-gradient(ellipse 50% 50% at 40% 80%, hsl(330 45% 45% / 0.12) 0%, transparent 50%)
          `
        }} />
        <div className="absolute inset-0 opacity-40" style={{
          backgroundImage: `
            radial-gradient(1.5px 1.5px at 15% 25%, hsl(200 90% 92%) 0%, transparent 100%),
            radial-gradient(2px 2px at 75% 55%, hsl(280 70% 88%) 0%, transparent 100%),
            radial-gradient(1px 1px at 45% 80%, hsl(190 80% 85%) 0%, transparent 100%),
            radial-gradient(1px 1px at 90% 10%, hsl(175 70% 80%) 0%, transparent 100%)
          `
        }} />
        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-5xl font-extrabold text-center mb-4 text-white">
            {isJa ? jaHome.safetyHeading : <>Your Safety <span className="text-gradient">Matters</span></>}
          </h2>
          <p className="text-white/60 text-center mb-16 max-w-2xl mx-auto text-lg">
            {isJa ? jaHome.safetyBody : "We're committed to creating a safe and respectful community for everyone."}
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              { icon: Shield, title: 'Safety Tools', desc: 'Block and report users who make you uncomfortable. Our moderation team reviews every report to keep the community safe.', delay: 0 },
              { icon: Users, title: 'Community Guidelines', desc: 'Clear rules, active moderation, and age verification ensure our community stays respectful, safe, and 18+ only.', delay: 150 },
            ].map((item, index) => ({ ...item, title: isJa ? jaHome.safety[index][0] : item.title, desc: isJa ? jaHome.safety[index][1] : item.desc })).map(({ icon: Icon, title, desc, delay }) => (
              <div
                key={title}
                className={`flex items-start gap-5 p-8 rounded-3xl border border-white/10 transition-all duration-500 ${
                  safetyView.inView ? 'animate-slide-up-fade opacity-100' : 'opacity-0 translate-y-8'
                }`}
                style={{
                  animationDelay: `${delay}ms`,
                  background: 'linear-gradient(135deg, hsl(260 25% 20% / 0.65), hsl(250 22% 16% / 0.75))',
                  backdropFilter: 'blur(16px)',
                }}
              >
                <div className="w-16 h-16 rounded-2xl flex-shrink-0 flex items-center justify-center shadow-glow" style={{ background: 'hsl(200 80% 55% / 0.15)' }}>
                  <Icon className="w-8 h-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2 text-white">{title}</h3>
                  <p className="text-white/50 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link to="/safety-tips" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-primary font-semibold text-lg spring-press hover:shadow-glow transition-shadow" style={{ background: 'hsl(260 25% 20% / 0.6)', backdropFilter: 'blur(12px)' }}>
              {isJa ? jaHome.safetyLink : 'Read our Safety Tips →'}
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 relative overflow-hidden" style={{ background: 'hsl(250 22% 12%)' }}>
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse 70% 60% at 50% 40%, hsl(200 70% 50% / 0.18) 0%, transparent 60%),
            radial-gradient(ellipse 60% 50% at 30% 80%, hsl(280 55% 50% / 0.14) 0%, transparent 55%)
          `
        }} />
        <div className="max-w-2xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-4 text-white">
            {isJa ? jaHome.contactHeading : <>Get In <span className="text-gradient">Touch</span></>}
          </h2>
          <p className="text-white/60 mb-8 text-lg">
            {isJa ? jaHome.contactBody : "Have questions or feedback? We'd love to hear from you."}
          </p>
          
          <div className="inline-flex items-center gap-3 text-lg px-8 py-4 rounded-2xl border border-white/10 shadow-card spring-press" style={{ background: 'hsl(260 25% 20% / 0.7)', backdropFilter: 'blur(16px)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'hsl(200 80% 55% / 0.15)' }}>
              <Mail className="w-5 h-5 text-primary" />
            </div>
            <a href="mailto:smashorpassdating.app@gmail.com" className="text-primary hover:underline font-semibold">
              smashorpassdating.app@gmail.com
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden" style={{ background: 'hsl(255 20% 11%)' }}>
        {/* Nebula clouds */}
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse 70% 60% at 20% 40%, hsl(210 70% 50% / 0.2) 0%, transparent 60%),
            radial-gradient(ellipse 80% 50% at 80% 60%, hsl(280 55% 50% / 0.18) 0%, transparent 55%),
            radial-gradient(ellipse 60% 40% at 50% 50%, hsl(175 65% 45% / 0.12) 0%, transparent 50%)
          `
        }} />
        <div className="absolute inset-0 opacity-35" style={{
          backgroundImage: `
            radial-gradient(1.5px 1.5px at 10% 50%, hsl(200 90% 90%) 0%, transparent 100%),
            radial-gradient(1px 1px at 90% 30%, hsl(280 70% 88%) 0%, transparent 100%),
            radial-gradient(1px 1px at 50% 90%, hsl(190 80% 85%) 0%, transparent 100%)
          `
        }} />
        <div className="max-w-2xl mx-auto text-center relative z-10">
          <div className="p-12 rounded-3xl border border-white/10 shadow-card" style={{ background: 'linear-gradient(135deg, hsl(260 25% 22% / 0.75), hsl(250 22% 18% / 0.85))', backdropFilter: 'blur(16px)' }}>
            <h2 className="text-3xl md:text-5xl font-extrabold mb-4 text-white">{isJa ? jaHome.ctaHeading : <>Ready to Find Your <span className="text-gradient">Smash</span>?</>}</h2>
            <p className="text-white/60 mb-10 text-lg">
              {isJa ? jaHome.ctaBody : 'Create your profile, discover nearby adults, and see where a mutual Smash can lead.'}
            </p>
            <Button size="lg" className="gradient-love text-primary-foreground shadow-glow text-lg h-14 px-10 spring-press" onClick={() => navigate('/auth')}>
              {isJa ? jaHome.ctaButton : 'Get Started Free'}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 relative overflow-hidden border-t border-white/10" style={{ background: 'hsl(255 20% 9%)' }}>
        <div className="absolute inset-0" style={{
          background: `
            radial-gradient(ellipse 60% 40% at 50% 0%, hsl(200 70% 50% / 0.1) 0%, transparent 60%),
            radial-gradient(ellipse 50% 40% at 80% 80%, hsl(280 55% 50% / 0.06) 0%, transparent 50%)
          `
        }} />
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full gradient-love flex items-center justify-center shadow-glow">
                <Gavel className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="font-extrabold text-gradient">Smash or Pass</span>
            </div>
            
            <nav className="flex flex-wrap items-center justify-center gap-6">
              <Link to="/guides" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[0] : 'Guides'}
              </Link>
              <Link to="/first-message-guide" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[1] : 'First Message Guide'}
              </Link>
              <Link to="/attachment-styles" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[2] : 'Attachment Styles'}
              </Link>
              <Link to="/dating-profile-tips" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[3] : 'Profile Tips'}
              </Link>
              <Link to="/smash-or-pass-uk" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[4] : 'Smash or Pass UK'}
              </Link>
              <Link to="/smash-or-pass-us" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[5] : 'Smash or Pass US'}
              </Link>
              <Link to="/game/pokemon-smash-or-pass" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[6] : 'Pokemon Quiz'}
              </Link>
              <Link to="/game/anime-smash-or-pass" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[7] : 'Anime Quiz'}
              </Link>
              <Link to="/terms-of-service" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[8] : 'Terms of Service'}
              </Link>
              <Link to="/privacy-policy" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[9] : 'Privacy Policy'}
              </Link>
              <Link to="/safety-tips" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[10] : 'Safety Tips'}
              </Link>
              <Link to="/faq" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[11] : 'FAQ'}
              </Link>
              <a href="mailto:smashorpassdating.app@gmail.com" className="text-sm text-white/40 hover:text-primary transition-colors">
                {isJa ? jaHome.footer[12] : 'Contact'}
              </a>
            </nav>
          </div>
          
          <div className="mt-8 pt-8 border-t border-white/10 text-center text-sm text-white/30">
            <p>© {new Date().getFullYear()} {isJa ? jaHome.copyright : 'Smash or Pass. All rights reserved.'}</p>
            <p className="mt-2">{isJa ? jaHome.tagline : 'SmashorPassDating.app - Making meaningful connections, one swipe at a time.'}</p>
          </div>
        </div>
      </footer>
    </div>;
};
export default Index;
